<template>
  <UContextMenu
    v-bind="{ ...$attrs, ...nativeProps, class: undefined }"
    :open="isOpen"
    :content="{ ...props.content, reference }"
    :ui="{
      content: normalizeClass([
        'p-context-menu surface border-gray-lighten-1 z-50 min-w-64 border shadow-none ring-0 focus:outline-hidden',
        $attrs.class
      ]),
      viewport: 'hidden'
    }"
    @update:open="onOpenChange"
  >
    <template #content-top>
      <div class="divide-gray-lighten-2 min-h-0 divide-y overflow-y-auto">
        <PContextMenuClipboard v-if="inputTarget" v-model="inputTarget" @close-menu="closeMenu" />
        <slot />
      </div>
    </template>
  </UContextMenu>
</template>

<script setup lang="ts">
import { normalizeClass, onMounted, onUnmounted, shallowRef, watch } from "vue"
import { storeToRefs } from "pinia"
import { reactivePick } from "@vueuse/core"
import UContextMenu, { type ContextMenuProps } from "@nuxt/ui/components/ContextMenu.vue"
import { useAppStore } from "@/store/app.store"
import { useContextMenuStore } from "@/store/context-menu.store"
import { useModelFormStore } from "@/store/model-forms.store"
import PContextMenuClipboard from "@/components/overlay/PContextMenuClipboard.vue"

type Props = Pick<
  ContextMenuProps,
  "size" | "content" | "portal" | "disabled" | "modal" | "pressOpenDelay"
> & {
  /** The element that originated the context-menu event. */
  target?: EventTarget
  /** Viewport location of the originating context-menu event. */
  position?: { x: number; y: number }
}
const props = withDefaults(defineProps<Props>(), { modal: false, portal: true })
const nativeProps = reactivePick(
  props,
  "size",
  "content",
  "portal",
  "disabled",
  "modal",
  "pressOpenDelay"
)
const emit = defineEmits<{
  closeMenu: []
}>()
defineOptions({ inheritAttrs: false })

const { cursorX, cursorY } = storeToRefs(useAppStore())
const contextMenuStore = useContextMenuStore()
const modelFormStore = useModelFormStore()
const inputTarget = shallowRef<HTMLInputElement | HTMLTextAreaElement | HTMLElement | null>(null)
if (props.target instanceof HTMLInputElement || props.target instanceof HTMLTextAreaElement) {
  inputTarget.value = props.target
} else if (props.target instanceof HTMLElement) {
  const contentEditable = props.target.closest<HTMLElement>("[contenteditable='true']")
  if (contentEditable?.isContentEditable) inputTarget.value = contentEditable
}

const isOpen = shallowRef(false)
const initialCursorX = props.position?.x ?? cursorX.value
const initialCursorY = props.position?.y ?? cursorY.value
const reference = {
  getBoundingClientRect: () => new DOMRect(initialCursorX, initialCursorY, 0, 0)
}

function closeMenu() {
  onOpenChange(false)
}

function onOpenChange(open: boolean) {
  contextMenuStore.isContextMenuOpen = open
  if (isOpen.value && !open) emit("closeMenu")
  isOpen.value = open
}

watch(
  () => modelFormStore.modelFormConfigs.map((config) => config.id),
  (ids, previousIds) => {
    const openedIds = ids.filter((id) => !previousIds.includes(id))
    const openedEditor = modelFormStore.modelFormConfigs.some(
      (config) => openedIds.includes(config.id) && config.model.isSaved
    )
    // Creation forms still need their originating combobox mounted for its save callbacks.
    if (openedEditor) closeMenu()
  }
)

onMounted(() => {
  if (!props.disabled) onOpenChange(true)
})

onUnmounted(() => {
  contextMenuStore.isContextMenuOpen = false
})
</script>
